import { useState, useMemo, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return articles
      .filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.excerpt.toLowerCase().includes(q) ||
          a.tags.some((t) => t.toLowerCase().includes(q)),
      )
      .slice(0, 6);
  }, [query]);

  return (
    <header className="sticky top-0 z-50 w-full">
      <div className="w-full bg-foreground-950 text-background-100">
        <div className="mx-auto flex h-9 max-w-[1240px] items-center gap-3 px-4 md:px-6">
          <span className="flex shrink-0 items-center gap-1.5 text-[11px] font-bold tracking-wide text-primary-400">
            <i className="ri-fire-line text-sm" aria-hidden="true" />
            今日热瓜
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="flex w-[200%] animate-ticker gap-8 whitespace-nowrap text-xs text-background-200">
              {[0, 1].map((dup) => (
                <div key={dup} className="flex shrink-0 gap-8">
                  {articles.slice(0, 6).map((a) => (
                    <Link
                      key={`${dup}-${a.slug}`}
                      to={`/article/${a.slug}`}
                      className="cursor-pointer transition-colors hover:text-primary-300"
                    >
                      {a.title}
                    </Link>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <span className="hidden shrink-0 items-center gap-1 text-[11px] text-background-300 md:flex">
            <i className="ri-calendar-line" aria-hidden="true" />
            2026-09-10
          </span>
        </div>
      </div>

      <div
        className={`w-full border-b border-background-200/80 transition-colors ${
          scrolled ? "bg-background-50/95 backdrop-blur-md" : "bg-background-50"
        }`}
      >
        <div className="mx-auto flex h-16 max-w-[1240px] items-center gap-6 px-4 md:h-[72px] md:px-6">
          <Link to="/" className="flex shrink-0 cursor-pointer items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 font-heading text-lg font-bold text-background-50">
              瓜
            </span>
            <span className="leading-tight">
              <span className="block font-heading text-lg font-bold text-foreground-950">
                爆瓜爆料社
              </span>
              <span className="hidden text-[11px] text-foreground-500 sm:block">
                全网热门爆料·每日更新
              </span>
            </span>
          </Link>

          <nav className="hidden flex-1 items-center gap-1 lg:flex">
            <Link
              to="/"
              className={`cursor-pointer rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                location.pathname === "/"
                  ? "bg-primary-100 text-primary-700"
                  : "text-foreground-700 hover:bg-background-100 hover:text-primary-700"
              }`}
            >
              首页
            </Link>
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className={`cursor-pointer rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  location.pathname === `/category/${c.slug}`
                    ? "bg-primary-100 text-primary-700"
                    : "text-foreground-700 hover:bg-background-100 hover:text-primary-700"
                }`}
              >
                {c.name}
              </Link>
            ))}
            <Link
              to="/about"
              className={`cursor-pointer rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                location.pathname === "/about"
                  ? "bg-primary-100 text-primary-700"
                  : "text-foreground-700 hover:bg-background-100 hover:text-primary-700"
              }`}
            >
              关于我们
            </Link>
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <div className="relative hidden md:block">
              <div className="flex items-center gap-2 rounded-full border border-background-300/70 bg-background-100/70 px-3 py-1.5 focus-within:border-primary-400">
                <i className="ri-search-line text-sm text-foreground-500" aria-hidden="true" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setSearchOpen(true);
                  }}
                  onFocus={() => setSearchOpen(true)}
                  onBlur={() => window.setTimeout(() => setSearchOpen(false), 160)}
                  placeholder="搜瓜、搜人、搜话题"
                  className="w-36 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none lg:w-44"
                />
              </div>
              {searchOpen && query.trim() && (
                <div className="absolute right-0 top-[calc(100%+8px)] w-80 overflow-hidden rounded-lg border border-background-200 bg-background-50">
                  {results.length > 0 ? (
                    results.map((a) => (
                      <Link
                        key={a.slug}
                        to={`/article/${a.slug}`}
                        className="flex cursor-pointer flex-col gap-0.5 border-b border-background-100 px-4 py-2.5 last:border-0 hover:bg-background-100"
                      >
                        <span className="line-clamp-1 text-sm font-medium text-foreground-900">
                          {a.title}
                        </span>
                        <span className="text-xs text-primary-600">{a.categoryName}</span>
                      </Link>
                    ))
                  ) : (
                    <p className="px-4 py-4 text-sm text-foreground-500">
                      没找到相关爆料，换个关键词试试
                    </p>
                  )}
                </div>
              )}
            </div>

            <Link
              to="/contact"
              className="hidden cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 md:flex"
            >
              <i className="ri-megaphone-line" aria-hidden="true" />
              我要爆料
            </Link>

            <button
              type="button"
              aria-label="打开菜单"
              onClick={() => setMobileOpen((v) => !v)}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300/70 text-foreground-800 lg:hidden"
            >
              <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="w-full border-b border-background-200 bg-background-50 lg:hidden">
          <nav className="mx-auto flex max-w-[1240px] flex-col px-4 py-3 md:px-6">
            <Link
              to="/"
              className="cursor-pointer border-b border-background-100 py-3 text-sm font-medium text-foreground-800"
            >
              首页
            </Link>
            {categories.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="flex cursor-pointer items-center gap-2 border-b border-background-100 py-3 text-sm font-medium text-foreground-800"
              >
                <i className={`${c.icon} text-primary-500`} aria-hidden="true" />
                {c.name}
              </Link>
            ))}
            <Link
              to="/about"
              className="cursor-pointer border-b border-background-100 py-3 text-sm font-medium text-foreground-800"
            >
              关于我们
            </Link>
            <Link
              to="/contact"
              className="mt-3 flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50"
            >
              <i className="ri-megaphone-line" aria-hidden="true" />
              我要爆料
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}